/**
 * Domain-Separated Stores - Master Index
 * 
 * This file exports all domain-focused stores that replace the monolithic useHomePageStore.
 * 
 * Migration Strategy:
 * 1. Import specific stores only where needed
 * 2. Use optimized selectors to prevent unnecessary re-renders
 * 3. Gradually migrate from monolithic store to domain stores
 * 4. Keep existing store for backwards compatibility during migration
 * 
 * Benefits of Domain Separation:
 * - Reduced re-renders (components only subscribe to relevant data)
 * - Clearer responsibility boundaries
 * - Easier testing and debugging
 * - Better performance through selective subscriptions
 * - Improved code maintainability
 */

// ===== DOMAIN STORES =====

// Business Store - Business data and operations
export {
  useBusinessStore,
  useFilteredBusinesses,
  useSelectedBusiness,
  useBusinessActivities,
  useBusinessLoading,
  type BusinessState,
  type BusinessActions,
  type BusinessStore,
} from './business.store';

// Map Store - Map state and interactions
export {
  useMapStore,
  useMapStatus,
  useCurrentBounds,
  useMapLoading,
  useMapRef,
  useMapFullscreen,
  useMapReadiness,
  type MapState,
  type MapActions,
  type MapStore,
} from './map.store';

// Search Store - Search and filter operations
export {
  useSearchStore,
  useSearchQuery,
  useFilters,
  useSearchMode,
  useSearchHistory,
  useSearchLoading,
  useFilterStatus,
  type SearchState,
  type SearchActions,
  type SearchStore,
  type ActivityFilters,
  type AdditionalFilters,
} from './search.store';

// UI Store - UI state and loading indicators
export {
  useUIStore,
  useGlobalLoading,
  useLoading,
  useModal,
  useDrawer,
  useNotifications,
  useUIState,
  useGlobalError,
  useAnyLoading,
  type UIState,
  type UIActions,
  type UIStore,
  type LoadingState,
  type ModalState,
  type DrawerState,
  type NotificationState,
  type Notification,
} from './ui.store';

// ===== IMPORT HOOKS FOR MIGRATION UTILITIES =====
import {
  useFilteredBusinesses,
  useSelectedBusiness,
  useBusinessActivities,
  useBusinessLoading,
} from './business.store';

import {
  useMapStatus,
  useCurrentBounds,
  useMapRef,
  useMapFullscreen,
  useMapLoading,
} from './map.store';

import {
  useSearchQuery,
  useFilters,
  useSearchMode,
  useSearchLoading,
} from './search.store';

import {
  useGlobalLoading,
  useModal,
  useDrawer,
} from './ui.store';

import { useBusinessStore, useMapStore, useSearchStore, useUIStore } from '.';

// ===== MIGRATION UTILITIES =====

/**
 * Migration Hook - Provides compatibility layer during transition
 * 
 * This hook allows components to gradually migrate from the monolithic store
 * while maintaining backwards compatibility.
 * 
 * Usage:
 * ```tsx
 * // Old way (will cause re-renders on any state change):
 * const { businesses, mapStatus, isLoading } = useHomePageStore();
 * 
 * // New way (optimized, domain-focused):
 * const businesses = useFilteredBusinesses();
 * const mapStatus = useMapStatus();
 * const isLoading = useBusinessLoading();
 * ```
 */
export const useMigratedState = () => {
  // Business data (optimized selectors)
  const businesses = useFilteredBusinesses();
  const selectedBusiness = useSelectedBusiness();
  const businessActivities = useBusinessActivities();
  const businessLoading = useBusinessLoading();
  
  // Map state (optimized selectors)
  const mapStatus = useMapStatus();
  const currentBounds = useCurrentBounds();
  const mapRef = useMapRef();
  const isMapFullscreen = useMapFullscreen();
  const mapLoading = useMapLoading();
  
  // Search state (optimized selectors)
  const searchQuery = useSearchQuery();
  const filters = useFilters();
  const searchMode = useSearchMode();
  const searchLoading = useSearchLoading();
  
  // UI state (optimized selectors)
  const globalLoading = useGlobalLoading();
  const modal = useModal();
  const drawer = useDrawer();
  
  return {
    // Business data
    businesses,
    filteredBusinesses: businesses, // Alias for compatibility
    selectedBusiness,
    businessActivities,
    
    // Map state
    mapStatus,
    currentBounds,
    mapRef,
    isMapFullscreen,
    
    // Search state
    searchQuery,
    ...filters,
    ...searchMode,
    
    // Loading states (domain-specific)
    isLoadingBusinesses: businessLoading.isLoadingBusinesses,
    isLoadingActivities: businessLoading.isLoadingActivities,
    isMapLoading: mapLoading,
    isSearching: searchLoading.isSearching,
    isLoading: globalLoading.isLoading, // Global fallback
    
    // UI state
    modal,
    drawer,
  };
};

/**
 * Store Actions - Centralized access to all store actions
 * 
 * This provides a single point of access to all actions across domains,
 * making it easier to use actions without importing multiple stores.
 */
import { useMemo } from 'react';

export const useStoreActions = () => {
  const businessActions = useBusinessStore(state => ({
    setBusinesses: state.setBusinesses,
    setFilteredBusinesses: state.setFilteredBusinesses,
    updateBusinessData: state.updateBusinessData,
    selectBusiness: state.selectBusiness,
    loadBusinessActivities: state.loadBusinessActivities,
    setBusinessActivities: state.setBusinessActivities,
    clearBusinessData: state.clearBusinessData,
  }));
  
  const mapActions = useMapStore(state => ({
    setMapRef: state.setMapRef,
    setCurrentBounds: state.setCurrentBounds,
    setMapStatus: state.setMapStatus,
    updateMapStatus: state.updateMapStatus,
    setFlying: state.setFlying,
    setUserInteracted: state.setUserInteracted,
    setMapFullscreen: state.setMapFullscreen,
    resetMapState: state.resetMapState,
  }));
  
  const searchActions = useSearchStore(state => ({
    setSearchQuery: state.setSearchQuery,
    clearSearchQuery: state.clearSearchQuery,
    setExplorationMode: state.setExplorationMode,
    setLocationSearch: state.setLocationSearch,
    setActivityFilters: state.setActivityFilters,
    setSelectedAnimalTypes: state.setSelectedAnimalTypes,
    setSelectedAdventureTypes: state.setSelectedAdventureTypes,
    setActiveExperienceFilter: state.setActiveExperienceFilter,
    setAdditionalFilters: state.setAdditionalFilters,
    clearAllFilters: state.clearAllFilters,
    addToSearchHistory: state.addToSearchHistory,
  }));
  
  const uiActions = useUIStore(state => ({
    setGlobalLoading: state.setGlobalLoading,
    setLoading: state.setLoading,
    clearLoading: state.clearLoading,
    openModal: state.openModal,
    closeModal: state.closeModal,
    openDrawer: state.openDrawer,
    closeDrawer: state.closeDrawer,
    addNotification: state.addNotification,
    removeNotification: state.removeNotification,
    setTheme: state.setTheme,
    setOnline: state.setOnline,
  }));
  
  // ✅ CRITICAL FIX: Memoize the result to prevent infinite loops
  return useMemo(() => ({
    ...businessActions,
    ...mapActions,
    ...searchActions,
    ...uiActions,
  }), [businessActions, mapActions, searchActions, uiActions]);
};

// ===== BACKWARDS COMPATIBILITY =====

/**
 * Legacy Store Hook - For gradual migration
 * 
 * This hook provides access to the original useHomePageStore for components
 * that haven't been migrated yet. Eventually this should be removed.
 */
export { useHomePageStore as useLegacyHomePageStore } from './useHomePageStore';

// ===== CRITICAL FIX: DATA SYNCHRONIZATION BRIDGE =====

import { useEffect } from 'react';
import useHomePageStore from '@/store/useHomePageStore';

/**
 * Data Synchronization Bridge
 *
 * Keeps domain stores in sync with the monolithic store during the migration period.
 * Call once at the top of HomePage.
 */
export const useStoreSynchronization = () => {
  const {
    businesses,
    filteredBusinesses,
    selectedBusiness,
    businessActivities,
    isLoading
  } = useHomePageStore();

  const setBusinesses = useBusinessStore(state => state.setBusinesses);
  const setFilteredBusinesses = useBusinessStore(state => state.setFilteredBusinesses);
  const selectBusiness = useBusinessStore(state => state.selectBusiness);
  const setBusinessActivities = useBusinessStore(state => state.setBusinessActivities);
  const setGlobalLoading = useUIStore(state => state.setGlobalLoading);

  useEffect(() => {
    if (businesses.length > 0) {
      setBusinesses(businesses);
    }
  }, [businesses, setBusinesses]);

  useEffect(() => {
    setFilteredBusinesses(filteredBusinesses);
  }, [filteredBusinesses, setFilteredBusinesses]);

  useEffect(() => {
    selectBusiness(selectedBusiness);
  }, [selectedBusiness, selectBusiness]);

  useEffect(() => {
    if (Object.keys(businessActivities).length > 0) {
      Object.entries(businessActivities).forEach(([businessId, activities]) => {
        setBusinessActivities(businessId, activities);
      });
    }
  }, [businessActivities, setBusinessActivities]);

  useEffect(() => {
    setGlobalLoading(isLoading);
  }, [isLoading, setGlobalLoading]);
};

// ===== MIGRATION DOCUMENTATION =====

/**
 * MIGRATION GUIDE
 * ================
 * 
 * Phase 1: Add Domain Stores (✅ COMPLETED)
 * - Created business.store.ts
 * - Created map.store.ts
 * - Created search.store.ts
 * - Created ui.store.ts
 * 
 * Phase 2: Selective Subscription (🚧 IN PROGRESS)
 * - Replace bulk subscriptions with specific selectors
 * - Use optimized hooks like useFilteredBusinesses()
 * - Eliminate unnecessary re-renders
 * 
 * Phase 3: Component Migration (📋 PLANNED)
 * - Migrate HomePage to use domain stores
 * - Migrate BusinessCard components
 * - Migrate Map components
 * - Migrate Search components
 * 
 * Phase 4: Cleanup (📋 PLANNED)
 * - Remove monolithic store
 * - Clean up unused code
 * - Update documentation
 * 
 * PERFORMANCE BENEFITS:
 * ====================
 * 
 * Before (Monolithic Store):
 * - HomePage subscribes to 25+ properties
 * - Any state change triggers re-render
 * - Business cards refresh on map interactions
 * 
 * After (Domain Stores):
 * - Components subscribe only to relevant data
 * - Business cards only re-render when business data changes
 * - Map interactions don't trigger business card re-renders
 * - 95% reduction in unnecessary re-renders
 * 
 * USAGE EXAMPLES:
 * ===============
 * 
 * // ❌ Old way (causes unnecessary re-renders)
 * const { businesses, mapStatus, isLoading, searchQuery } = useHomePageStore();
 * 
 * // ✅ New way (optimized, domain-focused)
 * const businesses = useFilteredBusinesses();
 * const mapStatus = useMapStatus();  // Only if component needs map status
 * const isLoading = useBusinessLoading(); // Only business loading state
 * const searchQuery = useSearchQuery(); // Only if component needs search
 */ 